// Geocoding API Service - Integration with Nominatim (OpenStreetMap)
// Documentation: https://nominatim.org/release-docs/develop/api/Search/
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { getApiUrl } from './apiUtils';

export interface GeocodingResult {
    lat: number;
    lon: number;
    displayName: string;
    success: boolean;
    error?: string;
    suggestions?: {
        lat: number;
        lon: number;
        displayName: string;
    }[];
}

export interface TimezoneResult {
    timezone: string;
    timezoneOffset: number; // minutes from UTC
    success: boolean;
}

interface NominatimResponse {
    lat: string;
    lon: string;
    display_name: string;
    importance: number;
}

const WRAPPED_RE = /^\s*[(\[]\s*([^()[\]]*?)\s*[)\]]\s*$/;
const COORD_PAIR_RE = /^([+-]?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*([+-]?\d{1,3}(?:\.\d+)?)$/;

/**
 * Reconnaît une saisie de type « lat, lon » (parenthèses ou crochets facultatifs, séparateur
 * virgule / point-virgule / espace, point décimal). Renvoie null hors bornes ou si le texte
 * n'est pas une paire de coordonnées, pour laisser la main au géocodeur.
 */
export function parseCoordinateQuery(query: string): { lat: number; lon: number } | null {
    const trimmed = query.trim();
    const inner = WRAPPED_RE.exec(trimmed)?.[1] ?? trimmed;
    const match = COORD_PAIR_RE.exec(inner);
    if (!match) return null;
    const lat = parseFloat(match[1]);
    const lon = parseFloat(match[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
    return { lat, lon };
}

/**
 * Géocode un nom de lieu en coordonnées via Nominatim (OSM)
 * @param locationName Nom du lieu (ex: "Paris, France")
 * @returns Coordonnées, nom complet et éventuelles suggestions
 */
export async function geocodeLocation(locationName: string): Promise<GeocodingResult> {
    if (!locationName || locationName.trim().length === 0) {
        return {
            lat: 0,
            lon: 0,
            displayName: '',
            success: false,
            error: 'planner.location.errorEmpty',
        };
    }

    // Paire de coordonnées saisie à la main : pas d'appel réseau (Nominatim rejette les parenthèses)
    const coords = parseCoordinateQuery(locationName);
    if (coords) {
        return {
            lat: coords.lat,
            lon: coords.lon,
            displayName: `${coords.lat}, ${coords.lon}`,
            success: true,
        };
    }

    const encodedLocation = encodeURIComponent(locationName.trim());
    
    try {
        let data: NominatimResponse[];

        if (Capacitor.isNativePlatform()) {
            const url = `https://nominatim.openstreetmap.org/search?q=${encodedLocation}&format=json&limit=10`;
            const response = await CapacitorHttp.get({
                url,
                headers: { 'User-Agent': 'SSI-Session-Planner/1.0' }
            });

            if (response.status !== 200) {
                throw new Error(`Nominatim API error: ${response.status}`);
            }
            data = response.data;
        } else {
            // Utilisation du proxy backend via getApiUrl pour éviter les erreurs CORS
            const url = getApiUrl(`/api/geocoding/search?q=${encodedLocation}&limit=10`);
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Geocoding proxy error: ${response.status}`);
            }
            data = await response.json();
        }

        if (data.length === 0) {
            return {
                lat: 0,
                lon: 0,
                displayName: '',
                success: false,
                error: 'planner.location.errorNotFound',
            };
        }

        // Le premier résultat reste le résultat principal par défaut
        const result = data[0];

        // On construit la liste des suggestions si plusieurs résultats
        const suggestions = data.map(item => ({
            lat: parseFloat(item.lat),
            lon: parseFloat(item.lon),
            displayName: item.display_name
        }));

        return {
            lat: parseFloat(result.lat),
            lon: parseFloat(result.lon),
            displayName: result.display_name,
            success: true,
            suggestions: suggestions.length > 1 ? suggestions : undefined
        };
    } catch (error) {
        console.error('Geocoding failed:', error);
        return {
            lat: 0,
            lon: 0,
            displayName: '',
            success: false,
            error: 'planner.location.errorFetch',
        };
    }
}

/**
 * Décalage UTC du site (minutes, positif à l'est) pour la nuit planifiée, heure d'été comprise.
 * Le décalage mémorisé avec le lieu est celui du jour où il a été récupéré : réutilisé tel quel,
 * il décale d'une heure les cibles après un changement d'heure, ou pour une date de l'autre saison.
 * Évalué à midi UTC du jour `date` (AAAA-MM-JJ) : la soirée reste dans le même régime, seule la
 * fin d'une nuit de changement d'heure (après 2 h ou 3 h) garde une heure d'écart.
 * Même calcul que tzOffsetMinutes (server/services/sentinelEngine.js).
 * Sans fuseau exploitable, renvoie `fallback` (ancien comportement, jamais UTC par défaut).
 */
export function siteOffsetForNight(timezone: string | undefined, date: string, fallback?: number): number | undefined {
    const [y, m, d] = (date || '').split('-').map(Number);
    if (!timezone || !y || !m || !d) return fallback;
    try {
        const instant = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
        const parts: Record<string, string> = {};
        const dtf = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        for (const part of dtf.formatToParts(instant)) parts[part.type] = part.value;
        const hour = parts.hour === '24' ? 0 : Number(parts.hour);
        const asUTC = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), hour, Number(parts.minute), Number(parts.second));
        return Math.round((asUTC - instant.getTime()) / 60000);
    } catch {
        return fallback; // fuseau inconnu du moteur Intl
    }
}

/**
 * Fetch timezone for a given latitude and longitude using Open-Meteo API
 */
export async function fetchTimezone(lat: number, lon: number): Promise<TimezoneResult> {
    const proxyPath = `/api/weather/forecast?latitude=${lat}&longitude=${lon}`;
    const url = getApiUrl(proxyPath);

    try {
        let data: any;

        if (Capacitor.isNativePlatform()) {
            const response = await CapacitorHttp.get({ url: url });
            if (response.status !== 200) throw new Error(`Timezone Proxy error: ${response.status} (Native)`);
            data = response.data;
        } else {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Timezone Proxy error: ${response.status} (Web)`);
            data = await response.json();
        }

        // Open-Meteo returns timezone ID (e.g. "Europe/Paris") and utc_offset_seconds
        const timezone = data.timezone || 'UTC';
        const offsetSeconds = data.utc_offset_seconds || 0;

        return {
            timezone,
            timezoneOffset: offsetSeconds / 60, // Convert to minutes
            success: true
        };
    } catch (error: any) {
        const errorMsg = error instanceof Error ? error.message : JSON.stringify(error);
        console.error(`[Timezone] Fetch failed via proxy: ${errorMsg}`);

        // Fallback: use browser local timezone
        const now = new Date();
        const browserOffset = -now.getTimezoneOffset(); // JS returns minutes but with inverted sign

        return {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            timezoneOffset: browserOffset,
            success: false
        };
    }
}

/**
 * Format coordinates for display
 */
export function formatCoordinates(lat: number, lon: number): string {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lonDir = lon >= 0 ? 'E' : 'O';
    return `${Math.abs(lat).toFixed(4)}° ${latDir}, ${Math.abs(lon).toFixed(4)}° ${lonDir}`;
}

